import styled, { keyframes } from 'styled-components';
import LockIconSvg from '~/assets/icons/lock.svg';

export const TranslationLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

export const HeaderArea = styled.div`
  flex-shrink: 0;
  padding: 72px 40px 0;
  background: #fff;

  @media (max-width: 900px) {
    padding: 0 16px;
  }
`;

export const ScrollWrapper = styled.div`
  flex: 1;
  overflow-y: scroll;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
  }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  /* Right padding is 32px + 8px scrollbar = 40px, matching HeaderArea */
  padding: 0 32px 48px 40px;

  @media (max-width: 900px) {
    padding: 0 16px 32px 16px;
  }
`;

export const TwoColumnHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  background: #fff;
  font-size: 14px;
  font-family: inherit;
  border: 1px solid #e0e0e0;
  border-bottom: none;
  border-radius: 12px 12px 0 0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    border-radius: 12px;
    gap: 0;
  }
`;

export const HeaderColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-right: 1px solid #e0e0e0;
  &:last-child {
    border-right: none;
  }

  @media (max-width: 900px) {
    border-right: none;
    padding: 12px 16px;
  }
`;

export const HeaderControls = styled(HeaderColumn)`
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 12px 16px;
`;

export const HeaderLabel = styled.div`
  font-family: inherit;
  font-size: inherit;
  font-weight: 500;
  color: #333;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const LockIcon = styled(LockIconSvg)`
  width: 16px;
  height: 16px;
  color: #4a4a4a;
  flex-shrink: 0;

  path {
    fill: currentColor;
  }
`;

export const Select = styled.select`
  background-color: white;
  color: black;
  border: 1px solid #ccc;
  border-radius: 6px;
  height: 36px;
  padding: 0 12px;
  min-width: 160px;
  font: inherit;
  box-sizing: border-box;
`;

export const Card = styled.div`
  border: 1px solid #e0e0e0;
  border-top: none;
  background: #fff;
`;

export const SectionDivider = styled.div`
  font-family: ralewayBold, Raleway, 'Helvetica Neue', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #333;
  height: 25px;
  min-height: 25px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #f9f9f9;
  border-top: 1px solid #ececec;
  border-bottom: 2px solid #9738ff;
`;

export const FieldLabel = styled.div`
  font-family: inherit;
  font-size: 12px;
  color: #888;
  margin-bottom: 0px;
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.2;
`;

export const TargetFieldHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 26px;
  min-height: 26px;
  margin-bottom: 0;
  line-height: 1;
`;

export const GenerateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  min-height: 26px;
  padding: 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  color: #333;
  line-height: 1;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f5f5f5;
    border-color: #ccc;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const SpinnerDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #aaa;
  border-top-color: transparent;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const MissingDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e53e3e;
`;

export const ReadonlyBox = styled.div`
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.35;
  color: #333;
  padding: 10px 12px;
  min-height: 40px;
  background: transparent;
  border-radius: 6px;
  width: 100%;
  box-sizing: border-box;
`;

export const Textarea = styled.textarea`
  width: 100%;
  min-height: 40px;
  padding: 10px 12px;
  border: none;
  border-radius: 6px;
  background: #f5f5f5;
  resize: vertical;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  color: #333;

  &::placeholder {
    color: #999;
  }

  &:focus {
    outline: none;
    background: #f0f0f0;
  }
`;

export const AutoTextarea = styled(Textarea)`
  resize: none;
  overflow: hidden;
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
`;

export const SectionRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  border-top: 1px solid #f2f2f2;

  &:first-child {
    border-top: none;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const FieldCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* 12px top + 12px bottom (+ 1px divider) ~= 25px between rows */
  padding: 12px 16px;
  border-right: 1px solid #f2f2f2;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 900px) {
    padding: 12px 16px;
    border-right: none;
    border-top: 1px solid #f5f5f5;
    &:first-child {
      border-top: none;
    }
  }
`;

export const CheckboxRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
`;

export const FilterDropdown = styled.div`
  position: relative;
  min-width: 0;
`;

export const FilterButton = styled.button`
  width: auto;
  text-align: left;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #fff;
  font: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  box-sizing: border-box;
`;

export const FilterBadgeCount = styled.span`
  background: #4f46e5;
  color: #fff;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
`;

export const FilterMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 200px;
  z-index: 50;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
`;

export const FilterSection = styled.div`
  &:not(:last-child) {
    border-bottom: 1px solid #eee;
    padding-bottom: 8px;
    margin-bottom: 8px;
  }
`;

export const FilterSectionLabel = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  color: #999;
  margin-bottom: 6px;
  letter-spacing: 0.05em;
`;

export const FilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  cursor: pointer;
  font-size: 13px;

  input {
    margin: 0;
  }

  &:hover {
    color: #4f46e5;
  }
`;

export const FilterCount = styled.span`
  font-size: 12px;
  color: #999;
`;

export const Badge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 11px;
  line-height: 14px;
  margin-left: 6px;
  background: #eee;
  color: #555;
`;

export const MissingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #b42318;
  background: #fee4e2;
  border: 1px solid #fecdca;
  border-radius: 999px;
  height: 26px;
  min-height: 26px;
  padding: 0 10px;
  line-height: 1;
  white-space: nowrap;
  box-sizing: border-box;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #f04438;
  }
`;

export const StaleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #b54708;
  background: #fffaeb;
  border: 1px solid #fedf89;
  border-radius: 999px;
  height: 26px;
  min-height: 26px;
  padding: 0 10px;
  line-height: 1;
  white-space: nowrap;
  box-sizing: border-box;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #f79009;
  }
`;

export const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const SkeletonLine = styled.div`
  height: 16px;
  width: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #f3f3f3 25%, #e9e9e9 37%, #f3f3f3 63%);
  background-size: 400% 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
`;

export const SkeletonPill = styled(SkeletonLine)`
  height: 32px;
`;
